import ReactDOM from 'react-dom'
import { EventEmitter } from '@sm/max-common'
import { IRouter } from '../types'
import { History } from './history'
import { getParentRoute, isSameDomain, loadPage } from './utils'
import { splitRoutes } from './split-route'
import { cachePageInstances, cacheSubPages } from './page'
import { routeAuth } from './auth-route'
import { config } from './preference'

declare let MAX_ROUTES: Record<string, string>
declare let GET_TOP_SCOPE: () => any

export function Router(): IRouter {
  const onRouteChangeEmitter = new EventEmitter<void>()
  const isSinglePageApp = config.isSinglePageApp
  const history = History(isSinglePageApp)
  let last = history.url

  const getPageKey = () => {
    return history.url
  }

  const spa = (route: string) => {
    const { created, removed } = splitRoutes(route, last)
    removed.forEach((route) => {
      const pageCode = MAX_ROUTES[route]
      const parentRoute = getParentRoute(route)
      const parentPageCode = MAX_ROUTES[parentRoute]
      if (parentPageCode) {
        const node = document.getElementById(parentPageCode)
        if (node) ReactDOM.unmountComponentAtNode(node)
      }
      if (cachePageInstances.has(pageCode)) {
        const page = cachePageInstances.get(pageCode)
        if (config.isAppSim || page.props?.isCache) page?.onHide?.()
      } else {
        const top = GET_TOP_SCOPE()
        try {
          top.del(pageCode)
        } catch (e) {
          console.error(e)
        }
      }
    })
    created.forEach((route) => {
      const pageCode = MAX_ROUTES[route]

      if (cacheSubPages.has(pageCode)) {
        const rerender = cacheSubPages.get(pageCode)
        setTimeout(() => {// 渲染spa时父节点没有挂载 子节点就渲染了 需要保证父节点完全挂载
          rerender(pageCode)
        })

      } else {
        loadPage(pageCode)
      }
    })
    last = route
  }

  const moveTo = (url: string, params?: any, replace = false) => {
    if (!params) params = {}
    if (isSameDomain(url, last)) {
      if (replace) {
        history.replaceState(params, '', `/${url}`)
      } else {
        history.pushState(params, '', `/${url}`)
      }
      spa(url)
    } else if (!isSinglePageApp) {
      if (replace) {
        history.replaceState(params, '', `/${url}`)
        location.replace(`/${url}`)
      } else {
        history.pushState(params, '', `/${url}`)
        location.assign(`/${url}`)
      }
    } else {
      cachePageInstances.clear()
      if (replace) {
        history.replaceState(params, '', `/${url}`)
      } else {
        history.pushState(params, '', `/${url}`)
      }
      spa(url)
    }
  }

  function navigateTo<T = any>(url: string, params?: T) {
    if (!routeAuth(url)) return
    let pageKey = url
    if (url.startsWith('/')) {
      pageKey = url.substring(1)
    }
    moveTo(pageKey, params)
    onRouteChangeEmitter.emit()
  }

  function navigateBack<T = any>(num = 1, params?: T) {
    /**
     * 1. 暂存参数，用完即消
     * 2. 解决history.replaceState失败，popstate事件触发后覆写了错误的state
     * 3. 仅针对history.(go|back|forward)等会产生popstate事件的API
     */
    history.popstate = params
    history.go(-num)
    if (isSinglePageApp) {
      spa(history.url)
    }
    onRouteChangeEmitter.emit()
  }

  function navigateBackTo<T = any>(url: string, params?: T) {
    if (!routeAuth(url)) return
    moveTo(url, params)
    onRouteChangeEmitter.emit()
  }

  function clearAllStack(pageKey?: string) {
    history.clear()
    if (isSinglePageApp) {
      spa(pageKey)
    } else {
      moveTo(pageKey)
    }
  }

  function redirectTo<T = any>(url: string, params?: T) {
    if (!routeAuth(url)) return
    last = ''
    moveTo(url, params, true)
    onRouteChangeEmitter.emit()
  }

  function getAllStack() {
    const pageId = getPageKey()
    return [
      {
        pageId,
        pageKey: pageId,
        instanceId: pageId,
      },
    ]
  }

  function getCurrent() {
    const pageId = getPageKey()
    const params = history.state
    return {
      pageId,
      pageKey: pageId,
      instanceId: pageId,
      params
    }
  }

  function getParams() {
    return history.state
  }

  function reload(options: Record<string, any>) {
    const { pageId, params } = options
    history.clear()
    cachePageInstances.clear()
    last = ''
    navigateTo(pageId, params)
  }

  window.addEventListener('popstate', (event) => {
    if (!isSinglePageApp && !isSameDomain(history.url, last)) {
      navigateTo(history.url)
    }
    if (history.popstate) {
      history.replaceState(history.popstate, '')
      history.popstate = null
    } else {
      history.replaceState(event.state, '')
    }
    spa(history.url)
    onRouteChangeEmitter.emit()
  })

  return {
    navigateTo,
    getParams,
    navigateBack,
    clearAllStack,
    navigateBackTo,
    redirectTo,
    getCurrent,
    getAllStack,
    reload,
    onChange: onRouteChangeEmitter.event,
  }
}
